import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';
import ApiService from '@/services/api';

// Add custom animations
const styles = `
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .animate-fadeIn {
    animation: fadeIn 0.2s ease-out;
  }
  
  .animate-slideUp {
    animation: slideUp 0.3s ease-out;
  }
`;

const ClosingPeriod = () => {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [expandedPeriod, setExpandedPeriod] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1
  });
  const [formData, setFormData] = useState({
    period_year: new Date().getFullYear(),
    period_month: new Date().getMonth() + 1,
    notes: ''
  });

  const monthNames = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page: page,
        limit: pagination.per_page
      };
      const response = await ApiService.getClosingPeriods(params);
      if (response.status === 200) {
        setPeriods(response.data || []);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      }
    } catch (error) {
      console.error('Error loading periods:', error);
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Gagal memuat data tutup buku!',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        period_year: parseInt(formData.period_year),
        period_month: parseInt(formData.period_month),
        notes: formData.notes || null
      };

      let response;
      if (editingPeriod) {
        response = await ApiService.updateClosingPeriod(editingPeriod.id, payload);
      } else {
        response = await ApiService.createClosingPeriod(payload);
      }

      if (response.status === 200 || response.status === 201) {
        Swal.fire({
          icon: 'success',
          title: 'Berhasil!',
          text: editingPeriod ? 'Period berhasil diupdate' : 'Period berhasil dibuat',
          timer: 1500,
          showConfirmButton: false
        });
        setShowModal(false);
        setEditingPeriod(null);
        resetForm();
        loadData();
      }
    } catch (error) {
      console.error('Error saving period:', error);
      Swal.fire({
        icon: 'error',
        title: 'Gagal!',
        text: error.response?.data?.error || 'Terjadi kesalahan saat menyimpan data',
      });
    }
  };

  const handleClosePeriod = async (period) => {
    const result = await Swal.fire({
      title: 'Tutup Buku?',
      html: `Apakah Anda yakin ingin menutup periode <strong>${monthNames[period.period_month - 1]} ${period.period_year}</strong>?<br><br>
        <small class="text-gray-600">Setelah ditutup, transaksi pada periode ini tidak dapat diubah.</small>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Ya, Tutup Buku!',
      cancelButtonText: 'Batal',
      input: 'textarea',
      inputPlaceholder: 'Catatan penutupan (opsional)...',
      inputAttributes: {
        'aria-label': 'Catatan penutupan'
      }
    });

    if (result.isConfirmed) {
      try {
        const response = await ApiService.closePeriod(period.id, {
          notes: result.value || null,
          closing_date: new Date().toISOString()
        });

        if (response.status === 200) {
          Swal.fire({
            icon: 'success',
            title: 'Berhasil!',
            text: 'Periode berhasil ditutup',
            timer: 1500,
            showConfirmButton: false
          });
          loadData();
        }
      } catch (error) {
        console.error('Error closing period:', error);
        Swal.fire({
          icon: 'error',
          title: 'Gagal!',
          text: error.response?.data?.error || 'Terjadi kesalahan saat menutup periode',
        });
      }
    }
  };

  const handleReopenPeriod = async (period) => {
    const result = await Swal.fire({
      title: 'Buka Kembali Period?',
      html: `Apakah Anda yakin ingin membuka kembali periode <strong>${monthNames[period.period_month - 1]} ${period.period_year}</strong>?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Ya, Buka Kembali!',
      cancelButtonText: 'Batal',
      input: 'textarea',
      inputPlaceholder: 'Alasan pembukaan kembali (wajib diisi)...',
      inputAttributes: {
        'aria-label': 'Alasan pembukaan kembali',
        required: true
      },
      inputValidator: (value) => {
        if (!value || value.length < 10) {
          return 'Alasan pembukaan harus diisi minimal 10 karakter!';
        }
      }
    });

    if (result.isConfirmed) {
      try {
        const response = await ApiService.reopenPeriod(period.id, {
          notes: result.value
        });

        if (response.status === 200) {
          Swal.fire({
            icon: 'success',
            title: 'Berhasil!',
            text: 'Periode berhasil dibuka kembali',
            timer: 1500,
            showConfirmButton: false
          });
          loadData();
        }
      } catch (error) {
        console.error('Error reopening period:', error);
        Swal.fire({
          icon: 'error',
          title: 'Gagal!',
          text: error.response?.data?.error || 'Terjadi kesalahan saat membuka periode',
        });
      }
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Hapus Period?',
      text: 'Data yang dihapus tidak dapat dikembalikan!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Ya, Hapus!',
      cancelButtonText: 'Batal'
    });

    if (result.isConfirmed) {
      try {
        const response = await ApiService.deleteClosingPeriod(id);
        if (response.status === 200) {
          Swal.fire({
            icon: 'success',
            title: 'Berhasil!',
            text: 'Period berhasil dihapus',
            timer: 1500,
            showConfirmButton: false
          });
          loadData();
        }
      } catch (error) {
        console.error('Error deleting period:', error);
        Swal.fire({
          icon: 'error',
          title: 'Gagal!',
          text: error.response?.data?.error || 'Terjadi kesalahan saat menghapus data',
        });
      }
    }
  };

  const handleEdit = (period) => {
    setEditingPeriod(period);
    setFormData({
      period_year: period.period_year,
      period_month: period.period_month,
      notes: period.notes || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      period_year: new Date().getFullYear(),
      period_month: new Date().getMonth() + 1,
      notes: ''
    });
    setEditingPeriod(null);
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gray-200 text-gray-700">Draft</span>,
      closed: <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-200 text-green-800">Ditutup</span>,
      reopened: <span className="px-3 py-1 text-xs font-semibold rounded-full bg-yellow-200 text-yellow-800">Dibuka Kembali</span>
    };
    return badges[status] || badges.draft;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const toggleExpand = (id) => {
    setExpandedPeriod(expandedPeriod === id ? null : id);
  };

  const handlePageChange = (newPage) => {
    loadData(newPage);
  };

  const renderPagination = () => {
    if (pagination.total_pages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, pagination.current_page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pagination.total_pages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-between mt-6 px-4">
        <div className="text-sm text-gray-600">
          Menampilkan {((pagination.current_page - 1) * pagination.per_page) + 1} - {Math.min(pagination.current_page * pagination.per_page, pagination.total)} dari {pagination.total} data
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handlePageChange(pagination.current_page - 1)}
            disabled={!pagination.prev_page}
            className={`px-3 py-1 rounded ${!pagination.prev_page ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
          >
            Prev
          </button>
          {startPage > 1 && (
            <>
              <button onClick={() => handlePageChange(1)} className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300">1</button>
              {startPage > 2 && <span className="px-2 py-1">...</span>}
            </>
          )}
          {pages.map(page => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-3 py-1 rounded ${page === pagination.current_page ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
            >
              {page}
            </button>
          ))}
          {endPage < pagination.total_pages && (
            <>
              {endPage < pagination.total_pages - 1 && <span className="px-2 py-1">...</span>}
              <button onClick={() => handlePageChange(pagination.total_pages)} className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300">{pagination.total_pages}</button>
            </>
          )}
          <button
            onClick={() => handlePageChange(pagination.current_page + 1)}
            disabled={!pagination.next_page}
            className={`px-3 py-1 rounded ${!pagination.next_page ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{styles}</style>
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link to="/dashboard" className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"></path>
              </svg>
              Dashboard
            </Link>
          </li>
          <li><span className="mx-2 text-gray-400">/</span></li>
          <li className="text-sm font-medium text-gray-500">Tutup Buku</li>
        </ol>
      </nav>

      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h5 className="text-xl font-semibold">Tutup Buku Periode</h5>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="btn btn-primary flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Buat Period Baru</span>
          </button>
        </div>

        <div className="card-body">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : periods.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Belum ada data tutup buku</p>
            </div>
          ) : (
            <div className="space-y-3">
              {periods.map((period) => (
                <div key={period.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h6 className="text-lg font-semibold">
                          {monthNames[period.period_month - 1]} {period.period_year}
                        </h6>
                        {getStatusBadge(period.status)}
                      </div>
                      {period.closing_date && (
                        <p className="text-sm text-gray-500 mt-1">
                          Ditutup: {new Date(period.closing_date).toLocaleDateString('id-ID', {
                            year: 'numeric', month: 'long', day: 'numeric'
                          })}
                        </p>
                      )}
                      {period.notes && (
                        <p className="text-sm text-gray-600 mt-1 italic">"{period.notes}"</p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {period.financial_summary && (
                        <button
                          onClick={() => toggleExpand(period.id)}
                          className="btn btn-sm btn-outline flex items-center gap-1"
                        >
                          {expandedPeriod === period.id ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          )}
                          <span>Summary</span>
                        </button>
                      )}

                      {period.status === 'draft' && (
                        <>
                          <button
                            onClick={() => handleEdit(period)}
                            className="btn btn-sm btn-info flex items-center gap-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span>Edit</span>
                          </button>
                          <button
                            onClick={() => handleClosePeriod(period)}
                            className="btn btn-sm btn-success flex items-center gap-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            <span>Tutup</span>
                          </button>
                          <button
                            onClick={() => handleDelete(period.id)}
                            className="btn btn-sm btn-danger flex items-center gap-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </>
                      )}

                      {period.status === 'closed' && (
                        <button
                          onClick={() => handleReopenPeriod(period)}
                          className="btn btn-sm btn-warning flex items-center gap-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                          </svg>
                          <span>Buka Kembali</span>
                        </button>
                      )}

                      {period.status === 'reopened' && (
                        <button
                          onClick={() => handleClosePeriod(period)}
                          className="btn btn-sm btn-success flex items-center gap-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <span>Tutup Kembali</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Financial Summary Expandable */}
                  {expandedPeriod === period.id && period.financial_summary && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                      <h6 className="font-semibold mb-3">Ringkasan Keuangan</h6>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">Total Aset</p>
                          <p className="text-lg font-semibold text-blue-600">{formatCurrency(period.financial_summary.total_assets)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Total Kewajiban</p>
                          <p className="text-lg font-semibold text-red-600">{formatCurrency(period.financial_summary.total_liabilities)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Total Ekuitas</p>
                          <p className="text-lg font-semibold text-green-600">{formatCurrency(period.financial_summary.total_equity)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Total Pendapatan</p>
                          <p className="text-lg font-semibold text-green-600">{formatCurrency(period.financial_summary.total_income)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Total Beban</p>
                          <p className="text-lg font-semibold text-red-600">{formatCurrency(period.financial_summary.total_expenses)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Laba Bersih</p>
                          <p className={`text-lg font-semibold ${period.financial_summary.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(period.financial_summary.net_profit)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Total Pinjaman Disalurkan</p>
                          <p className="text-lg font-semibold">{formatCurrency(period.financial_summary.total_loans_issued)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Total Angsuran Diterima</p>
                          <p className="text-lg font-semibold">{formatCurrency(period.financial_summary.total_loans_repaid)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Total Simpanan</p>
                          <p className="text-lg font-semibold">{formatCurrency(period.financial_summary.total_savings)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Jumlah Anggota</p>
                          <p className="text-lg font-semibold">{period.financial_summary.member_count}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Jumlah Transaksi</p>
                          <p className="text-lg font-semibold">{period.financial_summary.transaction_count}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Saldo Penutup</p>
                          <p className="text-lg font-semibold text-blue-600">{formatCurrency(period.financial_summary.closing_balance)}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {!loading && periods.length > 0 && renderPagination()}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg transform transition-all animate-slideUp">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-xl px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h5 className="text-xl font-bold text-white">
                      {editingPeriod ? 'Edit Period Tutup Buku' : 'Buat Period Tutup Buku Baru'}
                    </h5>
                    <p className="text-blue-100 text-sm">
                      {editingPeriod ? 'Perbarui informasi period' : 'Tambahkan period penutupan baru'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-5">
                {/* Year Input */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tahun Period <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <input
                      type="number"
                      className="form-control pl-10 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-lg"
                      value={formData.period_year}
                      onChange={(e) => setFormData({...formData, period_year: e.target.value})}
                      min="2000"
                      max="2100"
                      placeholder="Contoh: 2025"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Pilih tahun untuk period penutupan (2000-2100)</p>
                </div>

                {/* Month Select */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Bulan Period <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <select
                      className="form-control pl-10 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-lg appearance-none"
                      value={formData.period_month}
                      onChange={(e) => setFormData({...formData, period_month: e.target.value})}
                      required
                    >
                      {monthNames.map((month, index) => (
                        <option key={index} value={index + 1}>{month}</option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Pilih bulan untuk period penutupan</p>
                </div>

                {/* Notes Textarea */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Catatan
                    <span className="text-gray-400 font-normal ml-1">(Opsional)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute top-3 left-3 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </div>
                    <textarea
                      className="form-control pl-10 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-lg resize-none"
                      rows="4"
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Tambahkan catatan atau keterangan tambahan mengenai period ini..."
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Catatan ini akan membantu Anda mengingat detail period</p>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-blue-900">Informasi Penting</p>
                    <p className="text-xs text-blue-700 mt-1">
                      Period akan dibuat dengan status <span className="font-semibold">"Draft"</span>. 
                      Anda dapat mengedit atau menghapusnya sebelum ditutup.
                    </p>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Batal
                </button>
                <button 
                  type="submit" 
                  className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {editingPeriod ? 'Update Period' : 'Simpan Period'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ClosingPeriod;
